'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Keyboard, Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { Wrapper, SlideContent, BoldHeadingQuinaryDynamic } from './styles';
import styles from './styles.css';
import { withBreakpointsSlice } from '@/shared/providers/utils/with-breakpoints-slice';
import { BodyTextRegularPrimary } from '@/shared/ui/typography';
import { Button } from '@/shared/ui/form/button';
import { Box } from '@/shared/ui/box';
import { useRouter } from 'next-nprogress-bar';
import { useCurrentLocale } from '@locales/client';

import { useAuthModal } from '@/shared/hooks';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { useBanners } from './model/hooks';
import { resolveMediaPath } from '@/shared/libs/resolve-media-path';
import { BannerPrompts } from '@/features/data-managment/ui/banner-prompts';
import { LazyMedia } from './ui/lazy-media';
import { MotivationalModal } from './ui/motivational-modal';
import { useLocaleFormat } from '@/shared/hooks';
import { useVisibleState } from '@/shared/hooks';

const News = ({ mobileLandscape }) => {
  const locale = useCurrentLocale();
  const router = useRouter();
  const { user } = useUser();
  const { data, isLoading } = useBanners({ enabled: user.isAuth });
  const {
    open: openMotivationalModal,
    visible: visibleMotivationalModal,
    close: closeMotivationalModal,
  } = useVisibleState(false);
  const { handleOpenAuth } = useAuthModal();
  const formatUrl = useLocaleFormat();

  const isExternalLink = (url) =>
    url.startsWith('http://') || url.startsWith('https://');

  const handleClick = (url: string) => {
    const path = formatUrl(url);
    if (isExternalLink(url)) {
      window.open(path, '_blank');
    } else {
      router.push(path);
    }
  };

  const pagination = mobileLandscape
    ? {
        clickable: true,
        renderBullet: (index, className) =>
          `<span class="${className}"></span>`,
      }
    : false;

  if (!user.isAuth) {
    const videoSources = mobileLandscape
      ? `/banners/mobile/${locale}.mp4`
      : `/banners/desktop/${locale}.mp4`;

    const imageSrc = mobileLandscape
      ? `/images/background/banners/registration-banner-${locale}-mobile.jpg`
      : `/images/background/banners/registration-banner-${locale}.jpg`;

    return (
      <Wrapper>
        <Swiper
          loop
          loopAdditionalSlides={1}
          breakpoints={{
            640: { slidesPerView: 1, spaceBetween: 0 },
            768: { slidesPerView: 1, spaceBetween: 20 },
            960: { slidesPerView: 1, spaceBetween: 20 },
            1132: { slidesPerView: 1, spaceBetween: 20 },
          }}
          keyboard={{ enabled: true }}
          autoplay={{ delay: 1000000, stopOnLastSlide: true }}
          loopPreventsSliding={false}
          className={styles.swiper}
          modules={[Keyboard, Autoplay, Pagination]}
          autoHeight
        >
          <SwiperSlide>
            <SlideContent
              style={{ padding: 0 }}
              onClick={() => handleOpenAuth()}
              $locale={locale}
            >
              <LazyMedia
                videoSrc={videoSources}
                imageSrc={imageSrc}
                alt="Welcome banner"
                mobileLandscape={mobileLandscape}
              />
            </SlideContent>
          </SwiperSlide>
        </Swiper>
      </Wrapper>
    );
  }

  // user.isAuth&&(locale=='ro'||locale=='en')
  if (false) {
    const videoSources = null;
    // mobileLandscape
    //   ? `/banners/mobile/${locale}.mp4`
    //   : `/banners/desktop/${locale}.mp4`;

    const imageSrc = mobileLandscape
      ? `/images/background/banners/motivational/motivational-banner-${locale}-mobile.png`
      : `/images/background/banners/motivational/motivational-banner-${locale}-desktop.png`;

    return (
      <Wrapper>
        <Swiper
          loop
          loopAdditionalSlides={1}
          breakpoints={{
            640: { slidesPerView: 1, spaceBetween: 0 },
            768: { slidesPerView: 1, spaceBetween: 20 },
            960: { slidesPerView: 1, spaceBetween: 20 },
            1132: { slidesPerView: 1, spaceBetween: 20 },
          }}
          keyboard={{ enabled: true }}
          autoplay={{ delay: 1000000, stopOnLastSlide: true }}
          loopPreventsSliding={false}
          className={styles.swiper}
          modules={[Keyboard, Autoplay, Pagination]}
          autoHeight
        >
          <SwiperSlide>
            <SlideContent
              style={{ padding: 0 }}
              onClick={() => openMotivationalModal()}
              $locale={locale}
            >
              <LazyMedia
                videoSrc={videoSources}
                imageSrc={imageSrc}
                alt="motivational-banner"
                mobileLandscape={mobileLandscape}
              />
            </SlideContent>
          </SwiperSlide>
        </Swiper>
        <MotivationalModal
          visible={visibleMotivationalModal}
          onClose={closeMotivationalModal}
        />
      </Wrapper>
    );
  }

  if (isLoading) return null;

  return (
    <Wrapper>
      <BannerPrompts />
      <Swiper
        loop
        key={locale}
        loopAdditionalSlides={1}
        breakpoints={{
          640: { slidesPerView: 1, spaceBetween: 0 },
          768: { slidesPerView: 3, spaceBetween: 20 },
          960: { slidesPerView: 3, spaceBetween: 20 },
          1132: { slidesPerView: 3, spaceBetween: 20 },
        }}
        keyboard={{ enabled: true }}
        autoplay={{ delay: 1000000, stopOnLastSlide: true }}
        loopPreventsSliding={false}
        pagination={pagination}
        className={styles.swiper}
        modules={[Keyboard, Autoplay, Pagination]}
      >
        {data?.map((banner) => (
          <SwiperSlide key={banner?.id}>
            <SlideContent
              onClick={() => handleClick(banner?.link)}
              $type="promo"
            >
              <Box
                $position="absolute"
                $top="0"
                $left="0"
                $width="100%"
                $height="100%"
                $zIndex="-1"
              >
                <LazyMedia
                  videoSrc={
                    banner?.video ? resolveMediaPath(banner.video) : null
                  }
                  imageSrc={resolveMediaPath(banner?.image)}
                  alt={banner?.header || 'Banner'}
                  mobileLandscape={mobileLandscape}
                />
              </Box>

              <Box
                $fill
                $display="flex"
                $flexDirection="column"
                $textAlign="left"
                $justifyContent="space-between"
              >
                <Box>
                  <BoldHeadingQuinaryDynamic>
                    {banner?.header || (
                      <Box $visibility="hidden" $paddingBottom="12px" />
                    )}
                  </BoldHeadingQuinaryDynamic>
                  <BodyTextRegularPrimary
                    style={{
                      fontSize: mobileLandscape ? '14px' : '15px',
                      width: '140px',
                    }}
                  >
                    {banner?.text}
                  </BodyTextRegularPrimary>
                </Box>
                <Box
                  $textAlign={['left', 'center']}
                  $paddingBottom={[0, '16px']}
                >
                  <Button
                    style={{ fontWeight: 500 }}
                    variant="yellow"
                    onClick={() => router.push(banner?.link)}
                  >
                    <Box $padding={mobileLandscape ? '4px 16px' : '0 16px'}>
                      {banner?.btnTitle}
                    </Box>
                  </Button>
                </Box>
              </Box>
            </SlideContent>
          </SwiperSlide>
        ))}
      </Swiper>
    </Wrapper>
  );
};

const NewsWithBreakpointSlice = withBreakpointsSlice(
  News,
  ({ mobileLandscape }) => ({ mobileLandscape })
);

export { NewsWithBreakpointSlice as News };
